import React, { useState, useEffect, createContext, useContext } from 'react';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase/config';
import AdminLogin from './pages/AdminLogin';
import AdminLayout from './components/AdminLayout';
import Dashboard from './pages/Dashboard';
import DriversPage from './pages/DriversPage';
import CustomersPage from './pages/CustomersPage';
import RidesPage from './pages/RidesPage';
import LiveMapPage from './pages/LiveMapPage';
import ReportsPage from './pages/ReportsPage';

// ─── Auth Context ───────────────────────────────
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ─── Router (simple hash-based, no dependencies) ─
const ROUTES = {
  '/dashboard': <Dashboard />,
  '/drivers':   <DriversPage />,
  '/customers': <CustomersPage />,
  '/rides':     <RidesPage />,
  '/map':       <LiveMapPage />,
  '/reports':   <ReportsPage />,
};

function Router({ route }) {
  return ROUTES[route] || <Dashboard />;
}

// ─── Root App ───────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState(window.location.hash.slice(1) || '/dashboard');
  const auth = getAuth();

  useEffect(() => {
    const handler = () => setRoute(window.location.hash.slice(1) || '/dashboard');
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (snap.exists() && snap.data().role === 'admin') {
          setUser(firebaseUser);
          setUserRole('admin');
        } else {
          await signOut(auth);
          setUser(null);
        }
      } else {
        setUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = async (email, password) => {
    return await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => signOut(auth);

  if (loading) return <SplashScreen />;

  return (
    <AuthContext.Provider value={{ user, userRole, login, logout, route }}>
      {user ? (
        <AdminLayout>
          <Router route={route} />
        </AdminLayout>
      ) : (
        <AdminLogin />
      )}
    </AuthContext.Provider>
  );
}

function SplashScreen() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: '#0f172a', color: '#fff', fontFamily: 'Inter, sans-serif'
    }}>
      <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: -1, marginBottom: 8 }}>
        <span style={{ color: '#f59e0b' }}>AK</span> Dispatching
      </div>
      <div style={{ color: '#64748b', fontSize: 14 }}>Loading admin panel...</div>
      <div style={{
        marginTop: 32, width: 40, height: 40, border: '3px solid #1e293b',
        borderTopColor: '#f59e0b', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
